import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import StatsCard from '../StatsCard';
import './AdminDashboard.css';

interface AdminStats {
  users: {
    total: number;
    recent: number;
  };
  headaches: {
    total: number;
    recent: number;
  };
  tensions: {
    total: number;
    recent: number;
    averages: {
      avg_systolic: number | null;
      avg_diastolic: number | null;
      avg_pulse: number | null;
    };
  };
  lloros: {
    total: number;
    recent: number;
  };
  intensityDistribution: {
    intensity: string;
    count: number;
    percentage: number;
  }[];
  lloroIntensityDistribution: {
    intensity: string;
    count: number;
    percentage: number;
  }[];
}

interface ActivityItem {
  type: string;
  id: number;
  description: string;
  user_name: string;
  user_id: number;
  created_at: string;
  date?: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [impersonating, setImpersonating] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const [statsResponse, activityResponse] = await Promise.all([
        adminAPI.getDashboard(),
        adminAPI.getActivity({ limit: 15 })
      ]);
      setStats(statsResponse.data.data.statistics);
      setActivity(activityResponse.data.data.activity);
    } catch (err: any) {
      setError('Error al cargar estadisticas del dashboard');
      console.error('Error loading admin dashboard:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImpersonate = async (userId: number, userName: string) => {
    if (!window.confirm(`Iniciar sesion como "${userName}"?`)) return;

    try {
      setImpersonating(true);
      await startImpersonation(userId.toString());
      navigate('/dashboard');
    } catch (err: any) {
      setError('Error al impersonar usuario');
      setImpersonating(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'headache': return '🤕';
      case 'tension': return '🩺';
      case 'lloro': return '😢';
      case 'user_register': return '👤';
      default: return '📝';
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'headache': return '#ef4444';
      case 'tension': return '#3b82f6';
      case 'lloro': return '#8b5cf6';
      case 'user_register': return '#10b981';
      default: return '#6b7280';
    }
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando estadisticas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!stats) {
    return <div className="error-message">No se pudieron cargar las estadisticas</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Dashboard de Administrador</h1>
        <p>Resumen general del sistema MalDolor</p>
      </div>

      {/* Estadisticas principales */}
      <div className="stats-grid">
        <StatsCard
          title="Total Usuarios"
          value={stats.users.total}
          icon="👥"
          color="#3498db"
          subtitle={`+${stats.users.recent} ultimos 30d`}
        />
        <StatsCard
          title="Dolores de Cabeza"
          value={stats.headaches.total}
          icon="🤕"
          color="#e74c3c"
          subtitle={`+${stats.headaches.recent} ultimos 30d`}
        />
        <StatsCard
          title="Tensiones"
          value={stats.tensions.total}
          icon="🩺"
          color="#3b82f6"
          subtitle={`+${stats.tensions.recent} ultimos 30d`}
        />
        <StatsCard
          title="Lloros"
          value={stats.lloros.total}
          icon="😢"
          color="#8b5cf6"
          subtitle={`+${stats.lloros.recent} ultimos 30d`}
        />
      </div>

      {/* Promedios de tension */}
      {stats.tensions.averages && stats.tensions.averages.avg_systolic && (
        <div className="averages-card">
          <h3>Promedios de Tension Global</h3>
          <div className="averages-grid">
            <div className="average-item">
              <span className="average-label">Sistolica</span>
              <span className="average-value">{stats.tensions.averages.avg_systolic}</span>
              <span className="average-unit">mmHg</span>
            </div>
            <div className="average-item">
              <span className="average-label">Diastolica</span>
              <span className="average-value">{stats.tensions.averages.avg_diastolic}</span>
              <span className="average-unit">mmHg</span>
            </div>
            <div className="average-item">
              <span className="average-label">Pulso</span>
              <span className="average-value">{stats.tensions.averages.avg_pulse || '-'}</span>
              <span className="average-unit">ppm</span>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-grid">
        {/* Distribucion de intensidad - Dolores */}
        {stats.intensityDistribution.length > 0 && (
          <div className="intensity-analysis">
            <h2>Intensidad Dolores de Cabeza</h2>
            <div className="intensity-chart">
              <div className="intensity-bars">
                {stats.intensityDistribution.map((item) => (
                  <div key={item.intensity} className="intensity-bar">
                    <div className="bar-label">
                      <span className="intensity-name">
                        {item.intensity.charAt(0).toUpperCase() + item.intensity.slice(1)}
                      </span>
                      <span className="intensity-count">
                        {item.count} ({item.percentage || 0}%)
                      </span>
                    </div>
                    <div className="bar-container">
                      <div
                        className={`bar bar-${item.intensity}`}
                        style={{ width: `${item.percentage || 0}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Distribucion de intensidad - Lloros */}
        {stats.lloroIntensityDistribution && stats.lloroIntensityDistribution.length > 0 && (
          <div className="intensity-analysis lloros-analysis">
            <h2>Intensidad Lloros</h2>
            <div className="intensity-chart">
              <div className="intensity-bars">
                {stats.lloroIntensityDistribution.map((item) => (
                  <div key={item.intensity} className="intensity-bar">
                    <div className="bar-label">
                      <span className="intensity-name">
                        {item.intensity.charAt(0).toUpperCase() + item.intensity.slice(1)}
                      </span>
                      <span className="intensity-count">
                        {item.count} ({item.percentage || 0}%)
                      </span>
                    </div>
                    <div className="bar-container">
                      <div
                        className="bar bar-lloro"
                        style={{
                          width: `${item.percentage || 0}%`,
                          background: item.intensity === 'leve' ? '#10b981' :
                                     item.intensity === 'moderado' ? '#f59e0b' : '#ef4444'
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Feed de Actividad Reciente */}
      <div className="activity-feed">
        <div className="activity-header">
          <h2>Actividad Reciente</h2>
          <button onClick={loadDashboardData} className="refresh-btn">🔄</button>
        </div>
        <div className="activity-list">
          {activity.length === 0 ? (
            <div className="no-activity">No hay actividad reciente</div>
          ) : (
            activity.map((item, index) => (
              <div key={`${item.type}-${item.id}-${index}`} className="activity-item">
                <div
                  className="activity-icon"
                  style={{ backgroundColor: `${getActivityColor(item.type)}20`, color: getActivityColor(item.type) }}
                >
                  {getActivityIcon(item.type)}
                </div>
                <div className="activity-content">
                  <div className="activity-description">
                    <strong>{item.user_name}</strong> - {item.description}
                  </div>
                  <div className="activity-time">{formatTimeAgo(item.created_at)}</div>
                </div>
                {item.type !== 'user_register' && (
                  <button
                    className="impersonate-btn"
                    onClick={() => handleImpersonate(item.user_id, item.user_name)}
                    disabled={impersonating}
                    title="Ver como este usuario"
                  >
                    👁️
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Acciones rapidas */}
      <div className="quick-actions">
        <h2>Acciones Rapidas</h2>
        <div className="actions-grid">
          <button className="action-btn" onClick={() => navigate('/admin/users')}>
            <span className="action-icon">👥</span>
            <span className="action-label">Gestionar Usuarios</span>
          </button>
          <button className="action-btn" onClick={() => navigate('/admin/headaches')}>
            <span className="action-icon">🤕</span>
            <span className="action-label">Ver Dolores</span>
          </button>
          <button className="action-btn" onClick={() => navigate('/admin/tensions')}>
            <span className="action-icon">🩺</span>
            <span className="action-label">Ver Tensiones</span>
          </button>
          <button className="action-btn" onClick={() => navigate('/admin/reports')}>
            <span className="action-icon">📈</span>
            <span className="action-label">Generar Reportes</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
